import { useState } from "react";
import { Search, ChevronRight, ArrowLeft, Plus, Pencil } from "lucide-react";
import { WhatsAppAction } from "../components/WhatsAppAction";
import { useStore } from "../store";
import { api } from "../api";
import {
  dateKey,
  dateLabel,
  formatTime,
  durationLabel,
  type Client,
  type Appointment,
} from "../../shared/domain";
import { Modal, Empty, ErrorText } from "../components/ui";
import { AppointmentUndo } from "../components/AppointmentUndo";
import { AppointmentDetails } from "../components/AppointmentDetails";
import { AppointmentForm } from "../components/AppointmentForm";
const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("");
export function Clients() {
  const { data, refresh, offline } = useStore();
  const [details, setDetails] = useState<Appointment | null>(null);
  const [undo, setUndo] = useState<{ a: Appointment; until: number } | null>(
    null,
  );
  const [search, setSearch] = useState(""),
    [selected, setSelected] = useState<string | null>(null),
    [editing, setEditing] = useState<Client | "new" | null>(null),
    [adding, setAdding] = useState(false);
  const client = data!.clients.find((c) => c.id === selected),
    s = data!.settings;
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">FAMILIAR FACES. NEW STORIES.</p>
          <h1>
            Clients<span className="accent">.</span>
          </h1>
        </div>
        <button
          className="primary"
          disabled={offline}
          onClick={() => setEditing("new")}
        >
          <Plus size={18} />
          <span>Add client</span>
        </button>
      </div>
      {client ? (
        <div className="client-profile">
          <button className="text-button" onClick={() => setSelected(null)}>
            <ArrowLeft size={17} />
            All clients
          </button>
          <div className="profile-grid">
            <section className="profile-card">
              <div className="avatar large">{initials(client.name)}</div>
              <h2>{client.name}</h2>
              <p className="muted">{client.phone || "No phone number"}</p>
              <WhatsAppAction phone={client.phone} name={client.name} />
              <button
                className="primary wide"
                disabled={offline}
                onClick={() => setAdding(true)}
              >
                <Plus size={17} />
                New appointment
              </button>
              <button
                className="text-button"
                disabled={offline}
                onClick={() => setEditing(client)}
              >
                <Pencil size={15} />
                Edit client
              </button>
            </section>
            <section>
              <div className="section-heading">
                <h2>Appointment history</h2>
              </div>
              {!data!.appointments.some((a) => a.client_id === client.id) ? (
                <Empty
                  title="The story starts here."
                  description="This client's appointments will appear here."
                />
              ) : (
                data!.appointments
                  .filter((a) => a.client_id === client.id)
                  .sort((a, b) => b.start - a.start)
                  .map((a) => (
                    <button
                      className="history-card"
                      key={a.id}
                      aria-label={`View appointment for ${a.name} on ${dateKey(a.start, s.timezone)}`}
                      onClick={() => setDetails(a)}
                    >
                      <div>
                        <strong>
                          {dateLabel(dateKey(a.start, s.timezone))}
                        </strong>
                        <p className="muted">
                          {formatTime(a.start, s)} · {durationLabel(a.duration)}
                        </p>
                      </div>
                      <span className="history-label">
                        {a.start >= Date.now() ? "Upcoming" : "Past"}
                      </span>
                    </button>
                  ))
              )}
            </section>
          </div>
        </div>
      ) : (
        <>
          <label className="search">
            <Search size={18} />
            <input
              placeholder="Search clients…"
              aria-label="Search clients"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span>{data!.clients.length}</span>
          </label>
          <div className="client-list">
            {data!.clients
              .filter((c) =>
                (c.name + " " + c.phone)
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              )
              .map((c) => (
                <button
                  className="client-row"
                  key={c.id}
                  onClick={() => setSelected(c.id)}
                >
                  <span className="avatar">{initials(c.name)}</span>
                  <span>
                    <strong>{c.name}</strong>
                    <small>{c.phone || "No phone number"}</small>
                  </span>
                  <ChevronRight size={18} />
                </button>
              ))}
          </div>
          {!data!.clients.length && (
            <Empty
              title="Your people, in one place."
              description="Clients are saved automatically when you book an appointment."
            />
          )}
          {!!data!.clients.length &&
            !data!.clients.some((c) =>
              (c.name + " " + c.phone)
                .toLowerCase()
                .includes(search.toLowerCase()),
            ) && (
              <Empty
                title="No clients found."
                description="Try another name or phone number."
              />
            )}
        </>
      )}
      {details && (
        <AppointmentDetails
          appointment={
            data!.appointments.find((a) => a.id === details.id) ?? details
          }
          onClose={() => setDetails(null)}
          onDelete={(a, until) => setUndo({ a, until })}
        />
      )}
      {undo && <AppointmentUndo undo={undo} onClose={() => setUndo(null)} />}
      {editing && (
        <ClientEditor
          client={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onSave={() => void refresh()}
        />
      )}{" "}
      {adding && client && (
        <AppointmentForm
          client={client}
          date={dateKey(Date.now(), s.timezone)}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}
function ClientEditor({
  client,
  onClose,
  onSave,
}: {
  client?: Client;
  onClose: () => void;
  onSave: () => void;
}) {
  const { offline } = useStore();
  const [name, setName] = useState(client?.name || ""),
    [phone, setPhone] = useState(client?.phone || ""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <Modal title={client ? "Edit client" : "New client"} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (offline) return;
          setBusy(true);
          try {
            await api(
              client ? "/clients/" + client.id : "/clients",
              client ? "PUT" : "POST",
              { name, phone },
            );
            onSave();
            onClose();
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Full name
          <input
            autoFocus
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Phone number <small>optional</small>
          <input
            type="tel"
            maxLength={40}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <ErrorText text={error} />
        <button className="primary wide" disabled={busy || offline}>
          {busy ? "Saving…" : "Save client"}
        </button>
      </form>
    </Modal>
  );
}
